/**
 * Central API service & data adapters
 *
 * Backend field  →  component field
 * ─────────────────────────────────
 * id             →  _id  (all resources)
 * name           →  title  (products only)
 * base_price     →  price / originalPrice
 * sale_price     →  discountPercentage (computed %)
 * thumbnail_url  →  thumbnail
 * primary_image  →  thumbnail (product list)
 * stock_quantity →  stock
 * image_url      →  image  (categories)
 * logo_url       →  logo   (brands)
 * banner_url     →  banner
 */

import axios from 'axios'

const API_BASE = process.env.REACT_APP_API_URL || 'http://localhost:5000/api'
const MEDIA_BASE = API_BASE.replace(/\/api$/, '')

/**
 * Build a full URL for a stored media path.
 * Paths from the backend start with /uploads/...
 */
export const mediaUrl = (path) => {
  if (!path) return null
  if (path.startsWith('http') || path.startsWith('blob:') || path.startsWith('data:')) return path
  // Ensure single leading slash
  const clean = path.startsWith('/') ? path : `/${path}`
  return `${MEDIA_BASE}${clean}`
}

/** Prefer variant image when a cart/order line has variant_id */
export const resolveItemThumbnail = (item) => mediaUrl(
  item?.variant_primary_image ||
  item?.variant_image ||
  item?.thumbnail_url ||
  item?.primary_image ||
  item?.thumbnail ||
  null
)

// ─── Product normalizers ────────────────────────────────────────────────────

export const normalizeProduct = (p) => {
  if (!p) return p
  const price = parseFloat(p.base_price) || 0
  const salePrice = parseFloat(p.sale_price) || 0
  const discountPercentage = salePrice > 0 && salePrice < price
    ? Math.round(((price - salePrice) / price) * 100)
    : 0

  const rawThumb = p.thumbnail_url || p.primary_image || null

  return {
    ...p,
    _id: String(p.id ?? p._id),
    title: p.name || p.title || '',
    price: salePrice > 0 ? salePrice : price,
    originalPrice: price,
    discountPercentage,
    stock: p.stock_quantity ?? p.stock_available ?? p.stock ?? 0,
    thumbnail: mediaUrl(rawThumb),
    id: p.id ?? p._id,
    name: p.name || p.title || '',
    // Variant summary fields from listing API
    hasVariants: !!(p.has_variants),
    variantMinPrice: p.variant_min_price != null ? parseFloat(p.variant_min_price) : null,
    variantMaxPrice: p.variant_max_price != null ? parseFloat(p.variant_max_price) : null,
    // variant_colors is now [{name, color_code}] — normalize to consistent shape
    variantColors: (p.variant_colors || []).map(c =>
      typeof c === 'string' ? { name: c, color_code: null } : c
    ),
    variantSizes: p.variant_sizes || [],
    rating: Number(p.avg_rating ?? p.avgRating ?? p.rating ?? 0),
    reviewCount: Number(p.review_count ?? p.reviewCount ?? 0),
    qaEnabled: Boolean(p.qa_enabled ?? p.qaEnabled),
    secondaryImage: mediaUrl(
      p.secondary_image ||
      p.secondaryImage ||
      p.hover_image ||
      p.secondary_thumbnail ||
      null
    ),
  }
}

export const normalizeCategory = (c) => {
  if (!c) return c
  return {
    ...c,
    _id: String(c.id ?? c._id),
    image: mediaUrl(c.image_url || c.image),
    banner: mediaUrl(c.banner_url || c.banner),
    productCount: c.product_count ?? c.productCount ?? null,
  }
}

export const normalizeBrand = (b) => {
  if (!b) return b
  return {
    ...b,
    _id: String(b.id ?? b._id),
    logo: mediaUrl(b.logo_url || b.logo),
    banner: mediaUrl(b.banner_url || b.banner),
  }
}

export const normalizeProductDetail = (p) => {
  if (!p) return p
  const base = normalizeProduct(p)

  // Build images array — prefer product_media rows, fall back to product_images
  const rawImages = p.media || p.images || []
  const imageUrls = rawImages.map((img) =>
    mediaUrl(img.url || img.image_url || img) || ''
  ).filter(Boolean)

  // If no images found, try to use thumbnail_url, primary_image, or generate placeholder
  if (!imageUrls.length) {
    if (base.thumbnail) {
      imageUrls.push(base.thumbnail)
    } else {
      // Use placeholder image if no images at all
      imageUrls.push('/images/picture-loading-failed.svg')
    }
  }

  const variants = (p.variants || []).map((v) => {
    // Normalize variant images
    const variantImages = (v.images || []).map((img) =>
      mediaUrl(img.url || img.image_url || img) || ''
    ).filter(Boolean)
    
    return {
      ...v,
      _id: String(v.id),
      price: parseFloat(v.sale_price || v.price) || 0,
      originalPrice: parseFloat(v.price) || 0,
      stock: v.stock_available ?? v.stock ?? 0,
      images: variantImages,
    }
  })

  // For variant products, ALWAYS use variant stock aggregation, not base stock_quantity
  // stock_quantity is often the total inventory, not available-to-sell
  const variantStock = variants.reduce((sum, v) => sum + (Number(v.stock) || 0), 0)
  const resolvedStock = (p.has_variants && variants.length > 0) ? variantStock : base.stock

  return {
    ...base,
    images: imageUrls,
    description: p.description || p.short_description || '',
    shortDescription: p.short_description || '',
    brand: p.brand_name || p.brand || null,
    rating: p.avg_rating ?? p.rating ?? 0,
    stock: resolvedStock,
    variants,
    breadcrumb: p.breadcrumb || [],
    // Map of colorOptionName → hex code for rendering swatches
    colorCodes: p.color_codes || {},
    qaEnabled: Boolean(p.qa_enabled ?? p.qaEnabled),
    qaItems: (p.qa_items || []).map((item) => ({
      ...item,
      _id: String(item.id ?? item._id ?? item.display_order ?? item.question ?? ''),
      is_active: item.is_active !== false,
    })),
    upsellProducts: (p.upsell_products || []).map((item) => ({
      ...item,
      _id: String(item.id),
      price: parseFloat(item.sale_price || item.base_price) || 0,
      stock: item.stock_quantity ?? 0,
      thumbnail: mediaUrl(item.primary_image || item.thumbnail_url),
    })),
  }
}

// ─── Order normalizer ────────────────────────────────────────────────────────

export const normalizeOrder = (o) => {
  if (!o) return o
  const createdAt = o.created_at || o.createdAt || null
  return {
    ...o,
    _id: String(o.id ?? o._id),
    createdAt,
    created_at: createdAt,
    item_count: o.item_count ?? o.items?.length ?? 0,
    items: (o.items || o.cart || []).map((item) => ({
      ...item,
      _id: String(item.id ?? item._id),
      thumbnail: resolveItemThumbnail(item),
    })),
  }
}

// ─── Address normalizer ──────────────────────────────────────────────────────

export const normalizeAddress = (a) => {
  if (!a) return a
  return {
    ...a,
    _id: String(a.id ?? a._id),
  }
}

// ─── Review normalizer ───────────────────────────────────────────────────────

export const normalizeReview = (r) => {
  if (!r) return r
  return {
    ...r,
    _id: String(r.id ?? r._id),
    reviewerName: r.customer_name || r.reviewerName || 'Anonymous',
    date: r.created_at || r.date || '',
    comment: r.comment || r.body || '',
    rating: r.rating || 0,
  }
}

// ─── Offer / Content normalizer ─────────────────────────────────────────────

export const normalizeOffer = (o) => {
  if (!o) return o

  // Also normalize the product image URLs inside target_product
  let target_product = o.target_product || null
  if (target_product) {
    target_product = {
      ...target_product,
      thumbnail_url: mediaUrl(target_product.thumbnail_url),
      primary_image: mediaUrl(target_product.primary_image),
    }
  }

  return {
    ...o,
    _id: String(o.id ?? o._id),
    bannerImage: mediaUrl(o.banner_image || o.bannerImage),
    target_product,
  }
}

export const normalizeContentBlock = (b) => {
  if (!b) return b
  const linkedImage = b.linked_image || b.linked_product_media || b.linked_product_image
    || b.linked_category_image || b.linked_brand_image || null
  return {
    ...b,
    _id: String(b.id ?? b._id),
    linkedImageUrl: mediaUrl(linkedImage),
    imageUrl: mediaUrl(b.image_url || b.imageUrl) || mediaUrl(linkedImage),
    videoUrl: mediaUrl(b.video_url || b.videoUrl) || null,
    linked_slug: b.linked_slug || b.linked_product_slug || b.linked_category_slug || b.linked_brand_slug || null,
  }
}

// ─── Coupon normalizer ───────────────────────────────────────────────────────

export const normalizeCoupon = (c) => {
  if (!c) return c
  return {
    ...c,
    _id: String(c.id ?? c._id),
    minOrderValue: parseFloat(c.min_order_value || 0),
    maxDiscountAmount: c.max_discount_amount ? parseFloat(c.max_discount_amount) : null,
  }
}

// ─── Axios API client with auth interceptors ─────────────────────────────────

export const apiClient = axios.create({
  baseURL: API_BASE,
  withCredentials: true,
  headers: {
    // Bypass the ngrok browser-warning interstitial page (dev tunnels only).
    // This header is ignored by real servers, so safe to send always.
    'ngrok-skip-browser-warning': 'true',
  },
})

// Firebase token injection
apiClient.interceptors.request.use(async (config) => {
  // Try to get Firebase token from auth instance
  try {
    const { auth } = await import('../config/firebase')
    if (auth.currentUser) {
      const token = await auth.currentUser.getIdToken()
      config.headers.Authorization = `Bearer ${token}`
    }
  } catch (error) {
    // Firebase not initialized or no user, continue without token
    console.debug('No Firebase token available')
  }
  return config
})

apiClient.interceptors.response.use(
  (res) => res,
  async (error) => {
    const original = error.config
    if (error.response?.status === 401 && !original._retry) {
      original._retry = true
      try {
        // Try to refresh Firebase token
        const { auth } = await import('../config/firebase')
        if (auth.currentUser) {
          const token = await auth.currentUser.getIdToken(true) // force refresh
          original.headers.Authorization = `Bearer ${token}`
          return apiClient(original)
        }
      } catch {
        // Token refresh failed, redirect to login
        window.location.href = '/login'
      }
    }
    return Promise.reject(error)
  }
)

// ─── Grouped API methods ──────────────────────────────────────────────────────

export const clientApi = {
  auth: {
    login: (email, password) => apiClient.post('/client/auth/login', { email, password }),
    register: (data) => apiClient.post('/client/auth/register', data),
    logout: () => apiClient.post('/client/auth/logout'),
    forgotPassword: (email) => apiClient.post('/client/auth/forgot-password', { email }),
  },
  products: {
    list: (params) => apiClient.get('/client/products', { params }),
    get: (idOrSlug) => apiClient.get(`/client/products/${idOrSlug}`),
    search: (q) => apiClient.get('/client/products/search', { params: { q } }),
    notifyMe: (id, email) => apiClient.post(`/client/products/${id}/notify-me`, { email }),
  },
  categories: {
    list: () => apiClient.get('/client/categories'),
    get: (slugOrId) => apiClient.get(`/client/categories/${slugOrId}`),
  },
  brands: {
    list: () => apiClient.get('/client/brands'),
    get: (slugOrId) => apiClient.get(`/client/brands/${slugOrId}`),
  },
  cart: {
    get: (sessionId) => apiClient.get('/client/cart', { headers: sessionId ? { 'x-session-id': sessionId } : {} }),
    add: (data, sessionId) => apiClient.post('/client/cart/add', data, { headers: sessionId ? { 'x-session-id': sessionId } : {} }),
    update: (itemId, quantity, sessionId) => apiClient.put(`/client/cart/update/${itemId}`, { quantity }, { headers: sessionId ? { 'x-session-id': sessionId } : {} }),
    remove: (itemId, sessionId) => apiClient.delete(`/client/cart/remove/${itemId}`, { headers: sessionId ? { 'x-session-id': sessionId } : {} }),
    clear: (sessionId) => apiClient.delete('/client/cart/clear', { headers: sessionId ? { 'x-session-id': sessionId } : {} }),
    sync: (session_id) => apiClient.post('/client/cart/sync', { session_id }),
  },
  wishlist: {
    get: () => apiClient.get('/client/wishlist'),
    add: (product_id) => apiClient.post('/client/wishlist/add', { product_id }),
    remove: (productId) => apiClient.delete(`/client/wishlist/remove/${productId}`),
    check: (productId) => apiClient.get(`/client/wishlist/check/${productId}`),
  },
  orders: {
    list: (params) => apiClient.get('/client/orders', { params }),
    get: (id) => apiClient.get(`/client/orders/${id}`),
    place: (data) => apiClient.post('/client/orders', data),
    cancel: (id, reason) => apiClient.post(`/client/orders/${id}/cancel`, { reason }),
    tracking: (id) => apiClient.get(`/client/orders/${id}/tracking`),
    review: (id, data) => apiClient.post(`/client/orders/${id}/review`, data),
    uploadProof: (id, formData) => apiClient.post(`/client/orders/${id}/upload-proof`, formData, {
      headers: { 'Content-Type': 'multipart/form-data' },
    }),
  },
  profile: {
    get: () => apiClient.get('/client/profile'),
    update: (data) => apiClient.put('/client/profile', data),
    changePassword: (data) => apiClient.put('/client/profile/password', data),
    addresses: () => apiClient.get('/client/profile/addresses'),
    addAddress: (data) => apiClient.post('/client/profile/addresses', data),
    updateAddress: (id, data) => apiClient.put(`/client/profile/addresses/${id}`, data),
    deleteAddress: (id) => apiClient.delete(`/client/profile/addresses/${id}`),
    setDefaultAddress: (id) => apiClient.patch(`/client/profile/addresses/${id}/default`),
  },
  support: {
    list: (params) => apiClient.get('/client/support/tickets', { params }),
    get: (id) => apiClient.get(`/client/support/tickets/${id}`),
    create: (data) => apiClient.post('/client/support/tickets', data),
    reply: (id, message) => apiClient.post(`/client/support/tickets/${id}/message`, { message }),
    replyWithFiles: (id, formData) => apiClient.post(`/client/support/tickets/${id}/message`, formData, {
      headers: { 'Content-Type': 'multipart/form-data' }
    }),
  },
  coupons: {
    validate: (data) => apiClient.post('/client/coupons/validate', data),
  },
  paymentMethods: {
    list: (productId, params) => {
      const query = { ...params };
      if (productId) query.product_id = productId;
      return apiClient.get('/client/payment-methods', { params: query });
    },
  },
  offers: {
    active: () => apiClient.get('/client/offers/active'),
  },
  content: {
    list: (params) => apiClient.get('/client/content', { params }),
    page: (slug) => apiClient.get(`/client/content/page/${slug}`),
  },
  settings: {
    public: () => apiClient.get('/client/settings/public'),
  },
  reviews: {
    forProduct: (productId, params) => apiClient.get(`/client/reviews/product/${productId}`, { params }),
  },
  shipping: {
    calculate: (params) => apiClient.get('/client/shipping/calculate', { params }),
    states: () => apiClient.get('/client/shipping/states'),
  },
  notifications: {
    list: () => apiClient.get('/client/notifications'),
  },
  payments: {
    createRazorpayOrder: (orderId) => apiClient.post('/client/payments/razorpay/create-order', { order_id: orderId }),
    verifyRazorpayPayment: (data) => apiClient.post('/client/payments/razorpay/verify', data),
    reportFailed: (razorpayOrderId, errorDescription, errorCode, errorReason, orderId = null, attemptType = 'failed') => 
      apiClient.post('/client/payments/razorpay/failed', { 
        razorpay_order_id: razorpayOrderId, 
        order_id: orderId,
        error_description: errorDescription,
        error_code: errorCode,
        error_reason: errorReason,
        attempt_type: attemptType,
      }),
    reportModalClosed: (razorpayOrderId, reason) =>
      apiClient.post('/client/payments/razorpay/modal-closed', {
        razorpay_order_id: razorpayOrderId,
        reason,
      }),
    reportCancelled: (razorpayOrderId, reason) =>
      apiClient.post('/client/payments/razorpay/cancelled', {
        razorpay_order_id: razorpayOrderId,
        reason,
      }),
  },
}

export default API_BASE
